import { MouseEvent, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { ConnectionHandler } from "react-relay"
import { ArrowSquareOut, FileText, TrashSimple, WarningCircle } from "@phosphor-icons/react"
import { CircleProgress } from "@/components/circle-progress"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { TextWithTooltip } from "@/components/text-with-tooltip"
import { ListItem, ListText } from "@/components/ui/list"
import { formatDateString } from "@/lib/format-date"
import { DocumentViewerDialog } from "@/modules/patients/detail/conclude/document-viewer-dialog"
import { useDocumentStore } from "@/modules/patients/detail/store/useDocumentStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useDeleteMedicalReportMutation } from "@/modules/patients/services/useDeleteMedicalReport"
import {
  MedicalReportAttachment,
  MedicalReportAttachments as MedicalReportAttachmentsData,
} from "@/modules/patients/types/ProgressNoteDocument"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { FileUploadStatus } from "@/types/__generated__/union-types"

const WARNING_STATUSES: FileUploadStatus[] = ["MALICIOUS", "ERROR", "SCAN_TIMEOUT"]

interface MedicalReportsProps {
  medicalReportAttachments: MedicalReportAttachmentsData | undefined
  isEditMode: boolean
}

export const MedicalReportAttachments = (props: MedicalReportsProps) => {
  const { isEditMode, medicalReportAttachments } = props
  const { t } = useTranslation()
  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const appointmentId = useAppointmentParam()
  const country = useCountryParam()
  const setMedicalReportDocumentForViewing = useDocumentStore(
    (state) => state.actions.setMedicalReportDocumentForViewing,
  )
  const deleteMedicalReport = useDeleteMedicalReportMutation(country)

  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)
  const attachments = useMemo(() => {
    let othersIndex = 0
    let medicalReportsIndex = 0
    let lastSessionId = ""
    return (medicalReportAttachments?.edges ?? [])
      .toSorted((a, b) => (new Date(a.node.createdAt) < new Date(b.node.createdAt) ? 1 : 0))
      .map(({ node: attachment }) => {
        const isOtherDocument = attachment.reportType === "OTHER"
        const shouldIncrement = lastSessionId !== attachment.sessionId
        const documentLabel = t(`episodeOfCare.progressNote.${isOtherDocument ? "otherDocument" : "medicalReport"}`)
        const idx = isOtherDocument
          ? shouldIncrement
            ? ++othersIndex
            : othersIndex
          : shouldIncrement
            ? ++medicalReportsIndex
            : medicalReportsIndex
        const fileName = `${documentLabel} ${idx} - ${attachment.fileName}`
        lastSessionId = attachment.sessionId

        return { ...attachment, fileName }
      })
  }, [medicalReportAttachments?.edges, t])

  const handleRemoveMedicalReport = (event: MouseEvent<HTMLDivElement>, attachment: MedicalReportAttachment) => {
    event.stopPropagation()
    const connectionId = ConnectionHandler.getConnectionID(
      attachment.progressNoteId,
      "ProgressNoteConnection_medicalReportAttachments",
    )
    confirm({
      title: `${t("common.button.delete")} ${event.currentTarget.getAttribute("data-file")}`,
      titleTextClassName: "min-w-0 wrap-anywhere",
      titleCloseButtonClassName: "shrink-0",
      content: t("episodeOfCare.deleteProgressNote.content"),
      confirmVariant: "danger",
      confirmText: t("common.button.delete"),
      cancelText: t("common.button.keep"),
      onConfirm() {
        deleteMedicalReport({ id: attachment.id }, [connectionId])
        cancel()
      },
    })
  }

  const isEditable = isEditMode && !appointmentId

  return (
    <ScrollWrapper className="max-h-50 grid-cols-1">
      {attachments.map((attachment) => {
        return (
          <ListItem
            key={attachment.id}
            data-testid="medical-report-attachment-item"
            className="group hover:bg-surface-hovered flex h-12 items-center hover:cursor-pointer"
            onClick={() => {
              setMedicalReportDocumentForViewing(attachment)
              setIsOpenDialog(true)
            }}>
            {attachment.uploadStatus === "UPLOADED" ? (
              <CircleProgress className="shrink-0 animate-spin duration-1000" />
            ) : attachment.uploadStatus && WARNING_STATUSES.includes(attachment.uploadStatus) ? (
              <WarningCircle className="text-critical shrink-0" />
            ) : (
              <FileText className="text-on-surface-subdued shrink-0" />
            )}
            <div className="ml-2 min-w-0 group-hover:mr-4">
              <TextWithTooltip
                className="text-on-surface text-sm leading-5"
                tooltipContentClassName="wrap-anywhere"
                data-testid="medical-report-attachment-file-name">
                {attachment.fileName}
              </TextWithTooltip>
              <ListText variant="secondary" className={"truncate"}>
                {formatDateString(attachment.createdAt)}
              </ListText>
            </div>
            <div
              className="ml-auto hidden shrink-0 group-hover:block"
              data-testid="medical-report-attachment-action"
              data-file={attachment.fileName}
              onClick={isEditable ? (e) => handleRemoveMedicalReport(e, attachment) : undefined}>
              <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
                {isEditable ? (
                  <TrashSimple className="text-on-surface-subdued" />
                ) : (
                  <ArrowSquareOut className="text-on-surface-subdued" />
                )}
              </div>
            </div>
          </ListItem>
        )
      })}

      {isOpenDialog && <DocumentViewerDialog type="medicalReportAttachments" onOpenChange={setIsOpenDialog} />}
    </ScrollWrapper>
  )
}
